import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { destructiveDemoMessage } from "./demo-control-model";

/**
 * Confirmation for the panel's destructive actions, rendered inside the panel.
 *
 * These used to go through `window.confirm`. A native modal suspends the page
 * until it is dismissed, which suspends the Chrome extension with it: an agent
 * driving the panel clicks Reset, the dialog opens where it cannot see or
 * answer it, and every later command on that tab times out. The panel exists to
 * be driven, so it cannot use the one dialog that stops it being driveable.
 */
interface DemoConfirmValue {
  /** Ask, then run `action` only if the operator agrees. */
  confirmDestructive: (label: string, action: () => void) => void;
}

const DemoConfirmContext = createContext<DemoConfirmValue | null>(null);

interface Pending {
  label: string;
  action: () => void;
}

export function DemoConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirmDestructive = useCallback((label: string, action: () => void) => {
    setPending({ label, action });
  }, []);

  const value = useMemo(() => ({ confirmDestructive }), [confirmDestructive]);

  return (
    <DemoConfirmContext.Provider value={value}>
      {children}
      {pending ? (
        <div
          role="alertdialog"
          aria-label={`Confirm ${pending.label}`}
          className="relative z-20 shrink-0 border-white/60 border-t bg-white/90 p-3.5"
        >
          <p className="text-xs text-gray-700 leading-snug">
            {destructiveDemoMessage(pending.label)}
          </p>
          <div className="mt-2.5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPending(null)}
              className="rounded-md px-2.5 py-1 font-mono text-2xs text-gray-600 uppercase tracking-[0.1em] hover:bg-mist/70"
            >
              Cancel
            </button>
            <button
              type="button"
              // biome-ignore lint/a11y/noAutofocus: a confirm the operator just opened
              autoFocus
              onClick={() => {
                const { action } = pending;
                setPending(null);
                action();
              }}
              className="rounded-md bg-navy px-2.5 py-1 font-mono text-2xs text-white uppercase tracking-[0.1em] hover:bg-navy/85"
            >
              Continue
            </button>
          </div>
        </div>
      ) : null}
    </DemoConfirmContext.Provider>
  );
}

export function useDemoConfirm(): DemoConfirmValue {
  const ctx = useContext(DemoConfirmContext);
  if (!ctx) throw new Error("useDemoConfirm must be used inside DemoConfirmProvider");
  return ctx;
}
